import ControllerBase from './../../../../global/js/ControllerBase';
import { fromEvent } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
const template = require('components/molecules/m-typeahead-results/m-typeahead-results.template.twig') as any;
import { MoleculeTypeaheadResult } from 'wolf/components/molecules/m-typeahead-results/m-typeahead-results.data';
import { AtomButtonEvents } from 'wolf/components/atoms/a-button/a-button.events';

export default class ControllerKbStartpageStage extends ControllerBase {
	/** A list of dom element references available to this component */
	private readonly componentRefs = {
		searchBox: 'searchBox',
		searchResults: 'searchResults',
		searchButton: 'searchButton',
		searchForm: 'searchForm',
	};
	/** The number of typeahead results to show */
	private readonly numberOfResults = 3;

	/** Minimum number of characters that trigger a typehead search **/
	private readonly minCharLength = 2;
	/** The typeahead URL for typeahead queries */
	private typeaheadUrl: string;

	private searchUrl: string;
	/** A local cache for typeahead searches to reduce repeat queries */
	private cache: Map<string, MoleculeTypeaheadResult[]>;

	constructor(element: HTMLElement) {
		super(element);

		this.typeaheadUrl = element.dataset.typeaheadurl ?? '';
		this.searchUrl = element.dataset.searchurl ?? '';
		this.cache = new Map();
		// this.observeSearchButtonClick();
		this.observeUserSearch();
		this.observeSearchResults();
	}

	/**
	 * Handle synthetic form submits using an a-button component (instead of an input type=submit)
	 * @returns void
	 */
	private observeSearchButtonClick(): void {
		const buttonAtomComponent = this.$references[this.componentRefs.searchButton]!.firstChild;
		const form = this.$references[this.componentRefs.searchForm] as HTMLFormElement;
		if (form && buttonAtomComponent) {
			buttonAtomComponent.addEventListener(AtomButtonEvents.buttonClicked, (e: CustomEvent<Event | MouseEvent>) => {
				// if (e && e.detail && e.detail.quantity.toString().length > 0) {
				if (e && e.detail) {
					// submit the form
					form && form.submit();
				}
			});
		}
	}

	/**
	 * When the search results change, attach new event listeners to the result elements, to handle user clicks
	 * @returns void
	 */
	private observeSearchResults(): void {
		this.domUtils.onDomElementChanged.subscribe((ref) => {
			if (ref) {
				// @todo - find a better way to do this - the organism is now hardcoded to the molecule!
				const results = this.$element.querySelectorAll('.m-typeahead-results__results');
				if (results && results.length === 1) {
					const links = results[0].querySelectorAll('.m-typeahead-results__results-data a');
					if (links && links.length) {
						// attach event listeners for when the user clicks on a typeahead suggestion
						links.forEach((el: HTMLAnchorElement) => {
							el.addEventListener('click', (ev: Event | MouseEvent) => {
								ev.preventDefault();
								// populate the search box
								this.onTypeaheadSuggestionClick(el);
								// hide typeahead
								this.renderSearchResults([], true);
								// console.log('Do a search immediately?');
							});
						});
					}
				}
			}
		});
	}

	/**
	 * Populate the search box with the search item's text
	 * @param  {HTMLAnchorElement} element
	 * @returns void
	 */
	private onTypeaheadSuggestionClick(element: HTMLAnchorElement): void {
		// go directly to the search href
		const link: string = (element && element.dataset && element.dataset['href']) || '';
		if (link) {
			window.location.href = link;
		} else {
			// the below code will put the search result in the searchbox
			const searchBox = this.$references[this.componentRefs.searchBox] as HTMLInputElement;
			searchBox && (searchBox.value = element.text);
		}
	}

	/**
	 * Watch for search queries and update the typeahead results box
	 * @returns void
	 */
	private observeUserSearch(): void {
		if (Object.prototype.hasOwnProperty.call(this.$references, this.componentRefs.searchBox)) {
			/** Event handler for when the input changes value */
			const searchKeyupEvent$ = fromEvent(this.$references[this.componentRefs.searchBox], 'keyup');

			searchKeyupEvent$
				.pipe(
					debounceTime(120),
					map((e) => e && e.target),
				)
				.subscribe((e: HTMLInputElement) => {
					if (e && e.value.length >= this.minCharLength) {
						// report a search query
						this.dispatchCustomEvent(this.componentRefs.searchBox, e.value);

						const query = e.value as string;
						const cachedResults = this.cache.get(query);
						console.info('query', query);

						if (cachedResults) {
							console.info('using cached results', cachedResults);
							this.renderSearchResults(cachedResults);
							return;
						}

						this.queryTypeaheadResults(query)
							.then((response) => response.json())
							.then((searchResults: MoleculeTypeaheadResult[]) => {
								// console.info("using results from query", searchResults);
								this.cache.set(query, searchResults);
								this.renderSearchResults(searchResults);
							})
							.catch((e) => console.log(e));
					} else {
						this.renderSearchResults([], true);
					}
				});
		}
	}

	/**
	 * Render the search results onto the page
	 * @param  {MoleculeTypeaheadResult[]} items
	 * @returns void
	 */
	private renderSearchResults(items: MoleculeTypeaheadResult[], hideResults?: boolean): void {
		if (Object.prototype.hasOwnProperty.call(this.$references, this.componentRefs.searchResults)) {
			if (hideResults) {
				// hide results box completely
				this.$references[this.componentRefs.searchResults].innerHTML = '';
			} else {
				// build template
				const resultsHtml: string = template({ data: items });

				// render results
				this.domUtils.renderDomElement(this.componentRefs.searchResults, resultsHtml);
			}
		}
	}

	/**
	 * Do an API call for typeahead results
	 * @param  {string} query
	 * @returns Promise
	 */
	private queryTypeaheadResults(query: string): Promise<Response> {
		const url = new URL(this.typeaheadUrl);
		url.searchParams.set('q', query);
		return fetch(url.toString());
	}
}
